import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit'
import { iopsApi } from 'api'
import { generateChunks } from './js/generateChunks'

export interface UseParallelApiCalls {
  /** numeric utc start time */
  start_time?: number;
  /** numeric utc end time */
  end_time?: number;
  /** parallel calls at once = max is 6 default is 4 */
  parallel_call_max: 1 | 2 | 3 | 4 | 5 | 6;
  /** chunk time frame in ms default is 5 days */
  chunk_time?: number;
  /** api path */
  api_path: string;
  /** api args */
  api_args?: Record<string, unknown>;
  progressive_loading?: boolean;
}

export type UseParallelApiCallsStatus = 'waiting' | 'loading' | 'done' | 'error'

export interface UseParallelApiCallsChunkStats {
  start_time: number;
  end_time: number;
  status: UseParallelApiCallsStatus;
  rows: number;
  load_start?: number;
  load_end?: number;
  load_time?: number;
}

export interface UseParallelApiCallsResult {
  isUninitialized: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  data?: Record<string, unknown>;
  error?: unknown;
  startedTimeStamp?: number;
  fulfilledTimeStamp?: number;
}

export interface UseParallelApiCallsReturn {
  data: UseParallelApiCallsResult[]
  chunks: { start_time: number; end_time: number; status: UseParallelApiCallsStatus }[]
  stats: {
    load_start?: number;
    load_end?: number;
    load_time?: number;
    chunks: UseParallelApiCallsChunkStats[]
  }
}

interface ChunkResult {
  status: UseParallelApiCallsStatus;
  rows: number;
  result: UseParallelApiCallsResult;
  load_start?: number;
  load_end?: number;
  load_time?: number;
}

const WAITING_RESULT: UseParallelApiCallsResult = {
  isUninitialized: true,
  isLoading: false,
  isFetching: false,
  isSuccess: false,
  isError: false
}

const LOADING_RESULT: UseParallelApiCallsResult = {
  isUninitialized: false,
  isLoading: true,
  isFetching: true,
  isSuccess: false,
  isError: false
}

const rowsOf = (response: unknown): Record<string, unknown>[] => {
  if (Array.isArray(response)) return response as Record<string, unknown>[]
  const inner = (response as { data?: unknown } | undefined)?.data
  return Array.isArray(inner) ? inner as Record<string, unknown>[] : []
}

export const useParallelApiCalls = ({
  start_time,
  end_time,
  parallel_call_max = 4,
  chunk_time = 1000 * 60 * 60 * 24 * 5,
  api_path,
  api_args,
  progressive_loading = false,
}: UseParallelApiCalls): UseParallelApiCallsReturn => {
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>()
  const duration = (end_time || 0) - (start_time || 0)
  const api_args_key = JSON.stringify(api_args || {})
  const [run_results, setRunResults] = useState<{ run_key: string; results: ChunkResult[] }>({ run_key: '', results: [] })

  const chunks = useMemo(
    () => generateChunks({ start_time, end_time: (start_time || 0) + duration, chunk_time, progressive_loading }),
    [start_time, duration, chunk_time, progressive_loading]
  )

  const run_key = JSON.stringify([chunks, api_path, api_args_key, parallel_call_max, progressive_loading])

  const chunk_results = useMemo(
    () => run_results.run_key === run_key ? run_results.results : [],
    [run_results, run_key]
  )

  useEffect(() => {
    if (!api_path || !chunks.length) {
      setRunResults({ run_key, results: [] })
      return
    }
    let is_cancelled = false
    let next_index = chunks.length - 1
    const requests: { abort: () => void; unsubscribe: () => void }[] = []
    const args = JSON.parse(api_args_key) as Record<string, unknown>
    setRunResults({ run_key, results: chunks.map(() => ({ status: 'waiting', rows: 0, result: WAITING_RESULT })) })

    const updateChunk = (index: number, chunk_result: ChunkResult): void => {
      setRunResults(previous => previous.run_key !== run_key ? previous : {
        run_key,
        results: previous.results.map((item, item_index) => item_index === index ? chunk_result : item)
      })
    }

    const runQueue = async (): Promise<void> => {
      while (!is_cancelled && next_index >= 0) {
        const index = next_index
        next_index -= 1
        const chunk = chunks[index]
        const sent_time = Date.now()
        updateChunk(index, { status: 'loading', rows: 0, result: { ...LOADING_RESULT, startedTimeStamp: sent_time }, load_start: sent_time })
        const request = dispatch(iopsApi.endpoints.machineApis.initiate({
          ...args,
          path: api_path,
          utc_start_datetime: new Date(chunk.start_time).toISOString(),
          utc_end_datetime: new Date(chunk.end_time).toISOString()
        }, { forceRefetch: true }))
        requests.push(request)
        const result = await request
        request.unsubscribe()
        if (is_cancelled) return
        const load_start = result.startedTimeStamp
        const load_end = result.isError ? Date.now() : result.fulfilledTimeStamp
        updateChunk(index, {
          status: result.isError ? 'error' : 'done',
          rows: result.isError ? 0 : rowsOf(result.data).length,
          result: {
            isUninitialized: false,
            isLoading: false,
            isFetching: false,
            isSuccess: result.isSuccess,
            isError: result.isError,
            data: result.data,
            error: result.error,
            startedTimeStamp: result.startedTimeStamp,
            fulfilledTimeStamp: result.fulfilledTimeStamp
          },
          load_start,
          load_end,
          load_time: load_start !== undefined && load_end !== undefined ? load_end - load_start : undefined
        })
      }
    }

    for (let worker = 0; worker < Math.min(progressive_loading ? 4 : parallel_call_max, chunks.length); worker += 1) runQueue()

    return () => {
      is_cancelled = true
      requests.forEach(request => {
        request.abort()
        request.unsubscribe()
      })
    }
  }, [dispatch, chunks, api_path, api_args_key, parallel_call_max, progressive_loading, run_key])

  const api_returns = useMemo(
    () => chunks.map((_, index) => chunk_results[index]?.result || WAITING_RESULT),
    [chunks, chunk_results]
  )

  const stats = useMemo(() => {
    const chunk_stats: UseParallelApiCallsChunkStats[] = chunks.map((chunk, index) => ({
      ...chunk,
      status: chunk_results[index]?.status || 'waiting',
      rows: chunk_results[index]?.rows || 0,
      load_start: chunk_results[index]?.load_start,
      load_end: chunk_results[index]?.load_end,
      load_time: chunk_results[index]?.load_time
    }))
    const starts = chunk_stats.map(chunk => chunk.load_start).filter((value): value is number => value !== undefined)
    const ends = chunk_stats.map(chunk => chunk.load_end).filter((value): value is number => value !== undefined)
    const is_finished = chunk_stats.length > 0 && chunk_stats.every(chunk => chunk.status === 'done' || chunk.status === 'error')
    const load_start = starts.length ? Math.min(...starts) : undefined
    const load_end = is_finished && ends.length ? Math.max(...ends) : undefined
    return {
      load_start,
      load_end,
      load_time: load_start !== undefined && load_end !== undefined ? load_end - load_start : undefined,
      chunks: chunk_stats
    }
  }, [chunks, chunk_results])

  return {
    data: api_returns,
    chunks: stats.chunks.map(chunk => ({ start_time: chunk.start_time, end_time: chunk.end_time, status: chunk.status })),
    stats
  }

}
